import { Paths } from "@/paths";
import { Permission, PermissionLevels } from "@models/permission.model";

interface LandingRoute {
    feature: string;
    level: number;
    path: string;
}

// Priority order mirrors the sidebar (sidebar.tsx). First accessible entry
// wins as the post-login landing page.
export const LANDING_ROUTES: LandingRoute[] = [
    { feature: "Legacy Dashboard", level: PermissionLevels.VIEW,   path: Paths.CLIENT_DASHBOARD_LEGACY },
    { feature: "Dashboard",        level: PermissionLevels.VIEW,   path: Paths.HOME },
    { feature: "My Todos",         level: PermissionLevels.VIEW,   path: Paths.MY_TODOS },
    { feature: "Engagement Tool",  level: PermissionLevels.UPDATE, path: Paths.ENGAGEMENT },
    { feature: "Content Catalog",  level: PermissionLevels.VIEW,   path: Paths.CONTENT_CATALOG },
    { feature: "Moti Store",       level: PermissionLevels.VIEW,   path: Paths.MOTI_STORE },
    { feature: "Create Studio",    level: PermissionLevels.VIEW,   path: Paths.CREATE },
    { feature: "Ingest Files",     level: PermissionLevels.VIEW,   path: Paths.UPLOADS },
    { feature: "Organizations",    level: PermissionLevels.VIEW,   path: Paths.ORGANIZATIONS },
    { feature: "Permissions",      level: PermissionLevels.VIEW,   path: Paths.PERMISSIONS },
    { feature: "Jobs",             level: PermissionLevels.VIEW,   path: Paths.JOBS },
    { feature: "Tracer",           level: PermissionLevels.VIEW,   path: Paths.TRACER },
    { feature: "Config List",      level: PermissionLevels.VIEW,   path: Paths.CONFIG },
];

// First-path-segment → feature requirements (any one satisfies). Used to
// validate that a captured redirect URL points at a page the user can view.
// Unknown segments are treated as allowed so we don't accidentally block
// pages we haven't classified.
const SEGMENT_PERMISSIONS: Record<string, Array<{ feature: string; level: number }>> = {
    home:            [{ feature: "Dashboard",        level: PermissionLevels.VIEW }],
    "my-todos":      [{ feature: "My Todos",         level: PermissionLevels.VIEW }],
    engagements:     [{ feature: "Engagement Tool",  level: PermissionLevels.UPDATE }],
    contentCatalog:  [{ feature: "Content Catalog",  level: PermissionLevels.VIEW }],
    "moti-store":    [{ feature: "Moti Store",       level: PermissionLevels.VIEW }],
    create:          [{ feature: "Create Studio",    level: PermissionLevels.VIEW }],
    network_templates: [{ feature: "Network Presets", level: PermissionLevels.VIEW }],
    uploads:         [{ feature: "Ingest Files",     level: PermissionLevels.VIEW }],
    configs: [
        { feature: "Config List",  level: PermissionLevels.VIEW },
        { feature: "Ingest Files", level: PermissionLevels.VIEW },
    ],
    organizations:  [{ feature: "Organizations",    level: PermissionLevels.VIEW }],
    organization:   [{ feature: "Organizations",    level: PermissionLevels.VIEW }],
    permissions:    [{ feature: "Permissions",      level: PermissionLevels.VIEW }],
    "journey-steps": [{ feature: "Engagement Tool", level: PermissionLevels.VIEW }],
    jobs:           [{ feature: "Jobs",             level: PermissionLevels.VIEW }],
    tracer:         [{ feature: "Tracer",           level: PermissionLevels.VIEW }],
    banners:        [{ feature: "Manage Banners",   level: PermissionLevels.VIEW }],
    dashboards: [
        { feature: "Legacy Dashboard",     level: PermissionLevels.VIEW },
        // dashboards/op-launches*, op-launch-dashboard, op-reports (MPN-2229, MPN-2365): staff
        // pages under the same first segment.
        { feature: "Old Platform Reporting", level: PermissionLevels.VIEW },
    ],
};

/**
 * Returns the highest-priority landing path the user can access,
 * prefixed with BASE_URL. Returns undefined if none are accessible.
 */
export function pickLandingPath(perms?: Permission): string | undefined {
    const base = import.meta.env.BASE_URL;
    for (const route of LANDING_ROUTES) {
        if (perms?.hasSomeAccess?.(route.feature, route.level)) {
            return `${base}${route.path}`;
        }
    }
    return undefined;
}

/**
 * True if the user can view the page at `urlOrPath`. Unknown segments
 * return true to avoid blocking pages we haven't classified.
 */
export function canAccessUrl(urlOrPath: string, perms?: Permission): boolean {
    const pathname = urlOrPath.split("?")[0].split("#")[0];
    const segment = pathname.replace(/^\/+/, "").split("/")[0];
    if (!segment) return true;
    const requirements = SEGMENT_PERMISSIONS[segment];
    if (!requirements) return true;
    if (!perms) return false;
    return requirements.some(req => perms.hasSomeAccess?.(req.feature, req.level));
}
